/**
 * EditProfileDialog.jsx — modal that lets any signed-in user edit their
 * own public-facing profile fields (Name, Avatar, Bio, Pen Name).
 *
 * Wiring (Feb 26 2026):
 *   - PATCH /api/auth/profile   → name / bio / pen_name
 *   - POST  /api/auth/profile/avatar (multipart) → uploads to object
 *     Object Storage (mirrored from local disk) and writes the public URL
 *     onto `users.picture`. The dialog uploads the avatar FIRST so the
 *     subsequent PATCH always sees the freshest picture URL on the
 *     server side; we then re-fetch via the parent's `onSaved` hook so
 *     the Profile hero updates without a hard reload.
 */
import React, { useEffect, useRef, useState } from 'react';
import { Loader2, User as UserIcon, Camera, Save, PenTool, Globe } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { authFetch } from '../utils/authUtils';
import { COUNTRIES } from '../utils/countries';
import CountryCombobox from './CountryCombobox';
import CountryChangeVerifyDialog from './CountryChangeVerifyDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const formatApiError = (detail) => {
  if (!detail) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(' ');
  return String(detail);
};

const EditProfileDialog = ({ open, onOpenChange, user, isWriter = false, onSaved }) => {
  const [name, setName] = useState('');
  const [bio, setBio] = useState('');
  const [penName, setPenName] = useState('');
  const [country, setCountry] = useState('');
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  // Country-change verification state — the second-factor dialog opens
  // when the PATCH returns 409 with reason='cooldown_active'.
  const [verifyState, setVerifyState] = useState({ open: false, message: '', target: '' });
  const fileRef = useRef(null);

  const secsUntilFreeChange = Number(user?.country_next_free_change_in_seconds || 0);
  const cooldownActive = secsUntilFreeChange > 0;
  const cooldownLabel = (() => {
    if (!cooldownActive) return null;
    const days = Math.floor(secsUntilFreeChange / (24 * 3600));
    const hours = Math.floor((secsUntilFreeChange % (24 * 3600)) / 3600);
    return days > 0 ? `${days}d ${hours}h` : `${hours}h`;
  })();

  // Pre-fill whenever the dialog opens or the user payload changes.
  useEffect(() => {
    if (!open) return;
    setName(user?.name || '');
    setBio(user?.bio || '');
    setPenName(user?.pen_name || '');
    setCountry((user?.country || '').toUpperCase());
    setAvatarFile(null);
    setAvatarPreview(null);
  }, [open, user]);

  const close = (v) => {
    if (submitting) return;
    onOpenChange(v);
  };

  const handlePickAvatar = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith('image/')) {
      toast.error('Please choose an image file');
      return;
    }
    if (f.size > 5 * 1024 * 1024) {
      toast.error('Image too large (max 5MB)');
      return;
    }
    setAvatarFile(f);
    const reader = new FileReader();
    reader.onload = () => setAvatarPreview(reader.result);
    reader.readAsDataURL(f);
  };

  const patchProfile = async (extraPayload = {}) => {
    const payload = { name: name.trim(), bio: bio.trim(), country: country || '' };
    if (isWriter) payload.pen_name = penName.trim();
    Object.assign(payload, extraPayload);
    const r = await authFetch(`${BACKEND_URL}/api/auth/profile`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    const data = await r.json().catch(() => ({}));
    return { ok: r.ok, status: r.status, data };
  };

  const submit = async (e) => {
    e?.preventDefault();
    if (submitting) return;
    const trimmedName = name.trim();
    if (!trimmedName) {
      toast.error('Name is required');
      return;
    }
    setSubmitting(true);
    try {
      if (avatarFile) {
        const form = new FormData();
        form.append('avatar', avatarFile);
        const ar = await authFetch(`${BACKEND_URL}/api/auth/profile/avatar`, {
          method: 'POST',
          body: form,
        });
        const adata = await ar.json().catch(() => ({}));
        if (!ar.ok) throw new Error(formatApiError(adata.detail) || `Avatar upload failed (${ar.status})`);
      }

      const { ok, status, data } = await patchProfile();
      if (!ok) {
        // Cooldown-active → open verification dialog instead of erroring out.
        const detail = data?.detail;
        if (status === 409 && detail && typeof detail === 'object' && detail.reason === 'cooldown_active') {
          setVerifyState({
            open: true,
            message: detail.message || 'Additional country changes need verification.',
            target: (country || '').toUpperCase(),
          });
          return;   // dialog will handle re-submit
        }
        throw new Error(formatApiError(detail) || `Profile update failed (${status})`);
      }

      toast.success('Profile updated');
      onOpenChange(false);
      if (onSaved) onSaved();
    } catch (err) {
      toast.error(err.message || 'Could not save profile');
    } finally {
      setSubmitting(false);
    }
  };

  // Called by the verification dialog after the user completes both
  // location + password.
  const handleVerifiedResubmit = async (password, geoCountry) => {
    try {
      const { ok, status, data } = await patchProfile({
        verification_password: password,
        verification_geo_country: geoCountry,
      });
      if (!ok) {
        const detail = data?.detail;
        const msg = (detail && typeof detail === 'object' && detail.message) || formatApiError(detail) || `Update failed (${status})`;
        toast.error(msg);
        return;
      }
      setVerifyState({ open: false, message: '', target: '' });
      toast.success('Profile updated — country changed');
      onOpenChange(false);
      if (onSaved) onSaved();
    } catch (err) {
      toast.error(err?.message || 'Could not verify');
    }
  };

  const showPicture = avatarPreview || user?.picture;

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent
        className="bg-slate-900 border-slate-700 text-white max-w-md"
        data-testid="edit-profile-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <PenTool className="w-5 h-5 text-indigo-400" /> Edit Profile
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            Update your public name, avatar{isWriter ? ', pen name' : ''} and bio.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4">
          {/* Avatar picker */}
          <div className="flex items-center gap-4">
            <div className="relative">
              {showPicture ? (
                <img
                  src={showPicture}
                  alt="Avatar preview"
                  className="w-20 h-20 rounded-full object-cover ring-2 ring-indigo-500/40"
                  data-testid="edit-profile-avatar-preview"
                />
              ) : (
                <div className="w-20 h-20 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-full flex items-center justify-center">
                  <UserIcon className="w-10 h-10 text-white" />
                </div>
              )}
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                className="absolute -bottom-1 -right-1 p-2 rounded-full bg-indigo-600 hover:bg-indigo-700 text-white shadow-lg ring-2 ring-slate-900"
                aria-label="Change avatar"
                data-testid="edit-profile-avatar-btn"
              >
                <Camera className="w-3.5 h-3.5" />
              </button>
              <input
                ref={fileRef}
                type="file"
                accept="image/png,image/jpeg,image/webp,image/gif"
                className="hidden"
                onChange={handlePickAvatar}
                data-testid="edit-profile-avatar-input"
              />
            </div>
            <div className="text-xs text-slate-400 leading-snug">
              PNG, JPG, WEBP or GIF.<br />Max 5MB.
            </div>
          </div>

          {/* Name */}
          <div>
            <label className="text-xs text-slate-300 mb-1 block">Display name</label>
            <Input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={80}
              className="bg-slate-800 border-slate-700"
              data-testid="edit-profile-name-input"
              required
            />
          </div>

          {/* Pen name (scribes + admins only) */}
          {isWriter && (
            <div>
              <label className="text-xs text-slate-300 mb-1 block">
                Pen name <span className="text-slate-500 font-normal">(public author byline)</span>
              </label>
              <Input
                type="text"
                value={penName}
                onChange={(e) => setPenName(e.target.value)}
                maxLength={80}
                placeholder="Leave blank to use your display name"
                className="bg-slate-800 border-slate-700"
                data-testid="edit-profile-pen-name-input"
              />
            </div>
          )}

          {/* Bio */}
          <div>
            <label className="text-xs text-slate-300 mb-1 block">
              Bio <span className="text-slate-500 font-normal">({bio.length}/500)</span>
            </label>
            <textarea
              value={bio}
              onChange={(e) => setBio(e.target.value.slice(0, 500))}
              maxLength={500}
              rows={4}
              placeholder="Tell readers a little about yourself…"
              className="w-full rounded-md bg-slate-800 border border-slate-700 text-white text-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500/50"
              data-testid="edit-profile-bio-input"
            />
          </div>

          {/* Country override */}
          <div>
            <label className="text-xs text-slate-300 mb-1 flex items-center gap-1.5">
              <Globe className="w-3.5 h-3.5 text-indigo-300" /> Country
              <span className="text-slate-500 font-normal">· once every 7 days is free</span>
            </label>
            <CountryCombobox
              value={country}
              onChange={setCountry}
              placeholder="— Not specified —"
              testId="edit-profile-country-select"
              inputPaddingClass="py-2"
            />
            {cooldownActive ? (
              <p className="text-[11px] text-amber-300/90 mt-1.5" data-testid="edit-profile-country-cooldown">
                You recently changed your country. Next <strong>free</strong> change in{' '}
                <strong>{cooldownLabel}</strong>. Changing again sooner will ask you to verify with your password and turn on Location.
              </p>
            ) : (
              <p className="text-[11px] text-emerald-300/80 mt-1.5" data-testid="edit-profile-country-cooldown-ok">
                ✓ You can change your country freely right now.
              </p>
            )}
          </div>

          <DialogFooter className="pt-1">
            <Button
              type="button"
              variant="outline"
              onClick={() => close(false)}
              disabled={submitting}
              className="border-slate-600 text-slate-200"
              data-testid="edit-profile-cancel"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={submitting}
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
              data-testid="edit-profile-submit"
            >
              {submitting ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Save changes
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
      <CountryChangeVerifyDialog
        open={verifyState.open}
        targetCountry={verifyState.target}
        serverMessage={verifyState.message}
        onClose={() => setVerifyState({ open: false, message: '', target: '' })}
        onVerified={handleVerifiedResubmit}
      />
    </Dialog>
  );
};

export default EditProfileDialog;
